import { useEffect } from "react";
import DouglasvilleHeroSection from "./components/HeroSection";
import DouglasvilleStickyNav from "./components/StickyNav";
import DouglasvilleProcessingTimeSection from "./components/ProcessingTimeSection";
import DouglasvilleServicesSection from "./components/ServicesSection";
import DouglasvilleLocalAreaSection from "./components/LocalAreaSection";
import DouglasvilleTestimonialsSection from "./components/TestimonialsSection";
import DouglasvilleAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import DouglasvilleComparisonSection from "./components/ComparisonSection";
import DouglasvilleMistakesSection from "./components/MistakesSection";
import DouglasvilleHowItWorksSection from "./components/HowItWorksSection";
import DouglasvilleFAQSection, { faqs } from "./components/FAQSection";
import DouglasvilleHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import DouglasvilleFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function DouglasvillePage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Douglasville GA | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/douglasville/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Douglasville, GA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Douglas County and west metro Atlanta travelers. Get your emergency passport fast!");
    setMeta("geo.placename", "Douglasville, Georgia");
    setMeta("geo.region", "US-GA");
    setMeta("geo.position", "33.7515;-84.7477");
    setMeta("ICBM", "33.7515, -84.7477");
    setMeta("keywords", "expedited passport, emergency passport, Douglasville passport, same day passport Douglasville, fast passport Georgia, Douglas County passport");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Georgia", item: "https://fastpassportcenter.com/georgia/" },
          { "@type": "ListItem", position: 3, name: "Douglasville Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Douglasville — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Douglasville and west metro Atlanta travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Douglasville",
          containedInPlace: {
            "@type": "State",
            name: "Georgia",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Douglasville",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <DouglasvilleHeroSection />
      <DouglasvilleStickyNav />
      <DouglasvilleProcessingTimeSection />
      <DouglasvilleServicesSection />
      <DouglasvilleLocalAreaSection />
      <DouglasvilleTestimonialsSection />
      <DouglasvilleAcceptanceFacilitiesSection />
      <DouglasvilleComparisonSection />
      <DouglasvilleMistakesSection />
      <DouglasvilleHowItWorksSection />
      <DouglasvilleFAQSection />
      <DouglasvilleHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/douglasville" state="GA" />
      <DouglasvilleFinalCTA />
    </div>
  );
}